import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { invoiceAPI } from 'redux/invoiceAPI';
import { EditableLineItem } from '@payer-admin-ui/api/src/orders/models/LineItem';
import { ItemType } from '@payer-admin-ui/api/src/invoices/models/ItemType';
import { FetchingState } from '@payer-admin-ui/components-v2/src/types/state';
import { LocalDate } from '@payer-admin-ui/api/src/external/java/LocalDate';
import { toCents, toCentsOrNull, toNumber, toNumberOrNull } from '@payer-admin-ui/components-v2/src/functions/numbers';
import i18n from 'i18next';
import { fetchInvoice, upsertInvoiceInListView } from '../invoicesGlobalState';
import { showSnackbar } from '../../../../redux/snackbar';
import { MerchantAdminDispatch } from '../../../../redux/store';

export const creditInvoiceManually = createAsyncThunk<
  void,
  { invoiceNumber: number; invoiceDate?: LocalDate; standalone: boolean; item: EditableLineItem },
  { dispatch: MerchantAdminDispatch }
>(
  'invoices/actions/CreditInvoiceManuallyModal/credit',
  async ({ invoiceNumber, invoiceDate, standalone, item }, { dispatch }) => {
    if (
      !item.articleNumber ||
      !item.calculationMode ||
      !item.description ||
      !item.quantity ||
      !item.vatPercentage ||
      !item.subtotalPrice ||
      !item.subtotalVatAmount
    ) {
      throw new Error('Required data is missing');
    }
    const createdCreditInvoice = await invoiceAPI.actions.creditInvoiceManually({
      invoiceNumber,
      invoiceDate,
      standalone,
      items: [
        {
          itemType: ItemType.FREEFORM,
          calculationMode: item.calculationMode,
          position: 0,
          articleNumber: item.articleNumber,
          description: item.description,
          quantity: toNumber(item.quantity),
          unit: item.unit,
          unitPrice: toCentsOrNull(item.unitPrice) || undefined,
          unitVatAmount: toCentsOrNull(item.unitVatAmount) || undefined,
          vatPercentage: toCents(item.vatPercentage),
          subtotalPrice: toCents(item.subtotalPrice),
          subtotalVatAmount: toCents(item.subtotalVatAmount),
          salesAccountCode: toNumberOrNull(item.salesAccountCode) || undefined,
          vatAccountCode: toNumberOrNull(item.vatAccountCode) || undefined,
          additionalDetail: item.additionalDetail,
          costCenter: item.costCenter,
          ean: item.ean,
          uri: item.uri,
        },
      ],
    });
    dispatch(upsertInvoiceInListView(createdCreditInvoice));
    dispatch(fetchInvoice({ invoiceNumber }));
    dispatch(
      showSnackbar(i18n.t('invoices.actions.creditInvoiceManually.success', { invoiceNumber }), {
        severity: 'success',
      }),
    );
  },
);

export type CreditInvoiceManuallyModalState = {
  [invoiceNumber: string]: FetchingState | undefined;
};
const initialState: CreditInvoiceManuallyModalState = {};
const creditInvoiceManuallyModalSlice = createSlice({
  name: 'invoices/actions/CreditInvoiceManuallyModal',
  initialState,
  reducers: {
    reset: (state, { payload: { invoiceNumber } }) => {
      delete state[invoiceNumber];
    },
  },
  extraReducers: builder => {
    builder.addCase(creditInvoiceManually.pending, (state, { meta: { arg } }) => {
      state[arg.invoiceNumber] = { isFetching: true, success: undefined, error: null };
    });
    builder.addCase(creditInvoiceManually.fulfilled, (state, { meta: { arg } }) => {
      state[arg.invoiceNumber] = { isFetching: false, success: true, error: null };
    });
    builder.addCase(creditInvoiceManually.rejected, (state, { meta: { arg }, error }) => {
      state[arg.invoiceNumber] = { isFetching: false, success: false, error };
    });
  },
});

export const {
  actions: { reset },
  reducer: creditInvoiceManuallyModalReducer,
} = creditInvoiceManuallyModalSlice;
